/**
 * Main-thread handle to the database worker: request/response correlation over the
 * protocol of ./protocol, the tab lock of ./lock, the token relay of spec 9.7 step 1 and
 * the `simu:db-ready` performance mark. Platform-neutral so tests can drive it with a
 * fake worker; ./open.ts binds it to the real `new Worker(...)`.
 *
 * `ready` resolves when the worker reports `db:ready`. When another tab holds the
 * database it stays pending until `requestHandover()` succeeds; the shell (section 04)
 * watches `lockState` for that case and `ready.storage === 'memory'` for the banner
 * "Penyimpanan tetap tidak tersedia".
 */
import type { LocalWrite, Op, SyncScope } from '@satu/shared'
import { createTokenResponder, type TokenProvider } from '../sync/tokenBroker'
import { DbLock, type DbLockOptions, type DbLockState } from './lock'
import {
  type DbReadyInfo,
  INITIAL_SYNC_STATE,
  isResponse,
  type MainToWorker,
  type Requests,
  type RequestType,
  type ReservedRequestType,
  type SearchHit,
  type SearchRequest,
  type SnapshotProgress,
  type SyncSessionEvent,
  type SyncState,
  type WorkerError,
  type WorkerToMain,
} from './protocol'

export interface WorkerLike {
  postMessage(message: unknown): void
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
  addEventListener(type: 'error', listener: (event: unknown) => void): void
  terminate(): void
}

export type { TokenProvider }

export interface DbClientOptions {
  createWorker: () => WorkerLike
  /** Returns a fresh Supabase access token, or null offline / logged out (section 05). */
  tokenProvider?: TokenProvider
  /** Lock wiring; `false` runs without the one-tab rule (tests). */
  lock?: Omit<DbLockOptions, 'onState' | 'onPause' | 'onResume'> | false
  /** Respawn attempts when the OPFS pool is briefly held by a closing worker. */
  poolRetries?: number
  poolRetryMs?: number
  mark?: (name: string, detail: unknown) => void
  /**
   * Plan 10: the parish the scope's person works in (an admin keuskupan's stored choice), sent
   * with every `setScope`, so the worker and the screens open in the same place. Absent: none.
   */
  kerjaUntuk?: (scope: SyncScope) => string | null
  /**
   * Release 26: asked before every save (`ops:apply`, which `apply` sends too); an answer
   * refuses it before it reaches the worker (a device moved to the new address is read-only on
   * the old one). Null, or absent: saved as before.
   */
  tolakTulis?: () => WorkerError | null
}

export class DbClientError extends Error {
  readonly code: string

  constructor(error: WorkerError) {
    super(error.message)
    this.name = 'DbClientError'
    this.code = error.code
  }
}

export interface DbClient {
  readonly ready: Promise<DbReadyInfo>
  readonly lockState: DbLockState
  readonly syncState: SyncState
  request<K extends RequestType>(
    type: K,
    payload: Requests[K]['payload'],
  ): Promise<Requests[K]['result']>
  /** A reserved-namespace request (`register:*` and friends) with untyped payload. */
  requestRaw(type: ReservedRequestType, payload: unknown): Promise<unknown>
  /** `paroki`: the parish the screen worked in (plan 10); the worker refuses another. */
  apply(writes: LocalWrite[], paroki?: string | null): Promise<{ ops: Op[] }>
  setScope(scope: SyncScope | null): Promise<SyncState>
  syncNow(): Promise<SyncState>
  search(request: SearchRequest): Promise<SearchHit[]>
  setTokenProvider(provider: TokenProvider | null): void
  requestHandover(): Promise<boolean>
  onSyncState(listener: (state: SyncState) => void): () => void
  onSnapshotProgress(listener: (progress: SnapshotProgress) => void): () => void
  onSessionError(listener: (event: SyncSessionEvent) => void): () => void
  onLockState(listener: (state: DbLockState) => void): () => void
  close(): void
}

interface Pending {
  resolve: (value: unknown) => void
  reject: (error: Error) => void
}

function defaultMark(name: string, detail: unknown): void {
  const perf = (
    globalThis as { performance?: { mark?: (n: string, o?: { detail: unknown }) => unknown } }
  ).performance
  perf?.mark?.(name, { detail })
}

// Legacy mark name from SIMU; kept (pinned: test/nama-warisan.test.ts).
export const DB_READY_MARK = 'simu:db-ready'

export function createDbClient(options: DbClientOptions): DbClient {
  const pending = new Map<number, Pending>()
  const listeners = {
    sync: new Set<(state: SyncState) => void>(),
    snapshot: new Set<(progress: SnapshotProgress) => void>(),
    session: new Set<(event: SyncSessionEvent) => void>(),
    lock: new Set<(state: DbLockState) => void>(),
  }
  let worker: WorkerLike | null = null
  let seq = 0
  let closed = false
  let tokenProvider: TokenProvider | null = options.tokenProvider ?? null
  let syncState: SyncState = INITIAL_SYNC_STATE
  let lockState: DbLockState = { status: 'memulai' }
  let poolAttempts = 0
  const mark = options.mark ?? defaultMark

  let resolveReady: (info: DbReadyInfo) => void = () => {}
  let rejectReady: (error: Error) => void = () => {}
  const ready = new Promise<DbReadyInfo>((resolve, reject) => {
    resolveReady = resolve
    rejectReady = reject
  })
  // A client abandoned before the database opens must not surface an unhandled rejection.
  ready.catch(() => {})

  function failPending(error: Error): void {
    for (const p of pending.values()) p.reject(error)
    pending.clear()
  }

  function post(message: MainToWorker): void {
    if (worker === null)
      throw new DbClientError({ code: 'no_worker', message: 'database worker is not open' })
    worker.postMessage(message)
  }

  function request(type: string, payload: unknown): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (closed) {
        reject(new DbClientError({ code: 'closed', message: 'database client is closed' }))
        return
      }
      // Release 26: the one guard every save passes.
      const tolak = type === 'ops:apply' ? (options.tolakTulis?.() ?? null) : null
      if (tolak !== null) {
        reject(new DbClientError(tolak))
        return
      }
      const id = ++seq
      pending.set(id, { resolve, reject })
      try {
        post({ id, type: type as RequestType, payload })
      } catch (error) {
        pending.delete(id)
        reject(error instanceof Error ? error : new Error(String(error)))
      }
    })
  }

  const tokenResponder = createTokenResponder(() => tokenProvider)
  function answerToken(id: number): void {
    void tokenResponder.handle({ type: 'auth:token', id }, (message) => {
      if (worker !== null && !closed) post(message)
    })
  }

  function handle(message: WorkerToMain): void {
    if (isResponse(message)) {
      const waiting = pending.get(message.id)
      if (waiting === undefined) return
      pending.delete(message.id)
      if (message.ok) waiting.resolve(message.result)
      else waiting.reject(new DbClientError(message.error))
      return
    }
    switch (message.type) {
      case 'db:ready':
        if (message.info.storage === 'memory' && message.info.storageReason === 'busy' && respawn())
          return
        mark(DB_READY_MARK, message.info)
        resolveReady(message.info)
        return
      case 'db:error':
        rejectReady(new DbClientError(message.error))
        return
      case 'sync:state':
        syncState = message.state
        for (const l of listeners.sync) l(message.state)
        return
      case 'snapshot:progress':
        for (const l of listeners.snapshot) l(message.progress)
        return
      case 'sync:session':
        for (const l of listeners.session) l(message.session)
        return
      case 'auth:token':
        answerToken(message.id)
        return
    }
  }

  function spawn(): void {
    const created = options.createWorker()
    worker = created
    created.addEventListener('message', (event) => {
      if (worker === created) handle(event.data as WorkerToMain)
    })
    created.addEventListener('error', (event) => {
      const message = event instanceof Error ? event.message : 'database worker failed to start'
      const error = new DbClientError({ code: 'worker_error', message })
      rejectReady(error)
      failPending(error)
    })
  }

  /** The pool was held by a worker that is still closing: try again shortly. */
  function respawn(): boolean {
    const max = options.poolRetries ?? 3
    if (poolAttempts >= max) return false
    poolAttempts += 1
    const old = worker
    worker = null
    old?.terminate()
    setTimeout(
      () => {
        if (!closed) spawn()
      },
      (options.poolRetryMs ?? 150) * poolAttempts,
    )
    return true
  }

  const lock =
    options.lock === false
      ? null
      : new DbLock({
          ...(options.lock ?? {}),
          onState: (state) => {
            lockState = state
            for (const l of listeners.lock) l(state)
          },
          onPause: async () => {
            if (worker !== null) await request('db:pause', {})
          },
          onResume: async () => {
            if (worker === null) spawn()
            else await request('db:resume', {})
          },
        })

  async function start(): Promise<void> {
    try {
      if (lock === null || (await lock.acquire())) spawn()
    } catch (error) {
      rejectReady(error instanceof Error ? error : new Error(String(error)))
    }
  }
  void start()

  const subscribe =
    <T>(set: Set<(value: T) => void>) =>
    (listener: (value: T) => void) => {
      set.add(listener)
      return () => {
        set.delete(listener)
      }
    }

  return {
    ready,
    get lockState() {
      return lockState
    },
    get syncState() {
      return syncState
    },
    request: (type, payload) => request(type, payload) as never,
    requestRaw: (type, payload) => request(type, payload),
    apply: (writes, paroki) =>
      request('ops:apply', paroki === undefined ? { writes } : { writes, paroki }) as Promise<{
        ops: Op[]
      }>,
    setScope: (scope) =>
      request(
        'sesi:set',
        scope === null || options.kerjaUntuk === undefined
          ? { scope }
          : { scope, kerja: options.kerjaUntuk(scope) },
      ) as Promise<SyncState>,
    syncNow: () => request('sync:now', {}) as Promise<SyncState>,
    search: (req) => request('search', req) as Promise<SearchHit[]>,
    setTokenProvider(provider) {
      tokenProvider = provider
    },
    requestHandover: () => (lock === null ? Promise.resolve(true) : lock.requestHandover()),
    onSyncState: subscribe(listeners.sync),
    onSnapshotProgress: subscribe(listeners.snapshot),
    onSessionError: subscribe(listeners.session),
    onLockState: subscribe(listeners.lock),
    close() {
      if (closed) return
      closed = true
      const w = worker
      worker = null
      w?.terminate()
      lock?.close()
      failPending(new DbClientError({ code: 'closed', message: 'database client is closed' }))
    },
  }
}
